import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Informational product page. Content is driven by props.
 * All CTAs route to the Home lead-capture form — no in-page PII collection.
 */
export default function ProductPage({ overline, title, italicWord, subtitle, hero, columns }) {
  return (
    <div className="text-white">
      <section className="relative overflow-hidden pt-16 pb-20 ef-hero-radial ef-noise">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1481026469463-66327c86e544?w=1600&q=80')] bg-cover bg-center opacity-[0.10]" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="ef-overline">{overline}</div>
            <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 tracking-tight leading-[1]">
              {title} <em className="ef-gold-text not-italic">{italicWord}</em>
            </motion.h1>
            <p className="mt-6 text-white/70 max-w-xl text-lg leading-relaxed">{subtitle}</p>
            <div className="mt-8 flex gap-3">
              <a href="/#request-info-form">
                <Button data-testid="product-request-info" className="ef-btn-primary rounded-full px-6 h-12">
                  Request Information <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </a>
              <a href="tel:+17865989004">
                <Button data-testid="product-contact" variant="outline" className="ef-btn-ghost rounded-full px-6 h-12 bg-transparent text-white">Contact us</Button>
              </a>
            </div>
          </div>
          {hero?.image && (
            <div className="lg:col-span-5">
              <div className="ef-glass rounded-3xl overflow-hidden">
                <img src={hero.image} alt={hero.alt || title} className="w-full aspect-[4/5] object-cover opacity-90" />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-[#050f22]">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {columns.map((col) => (
              <div key={col.title} className="ef-card p-7" data-testid={`col-${col.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="ef-overline">{col.tag || "Info"}</div>
                <div className="font-display text-2xl font-bold mt-3">{col.title}</div>
                <p className="text-sm text-white/60 mt-2 leading-relaxed">{col.desc}</p>
                {col.bullets?.length ? (
                  <ul className="mt-5 space-y-2">
                    {col.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-white/75"><CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-[#17A673] shrink-0" /> {b}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
